import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { Spinner } from 'baseui/spinner';
import clsx from 'clsx';
import { GET_COMPANY_JOBS } from '../../query/jobs';
import { GetCompanyJobs, GetCompanyJobsVariables } from '../../query/__generated__/GetCompanyJobs';
import { JobStatusEnum } from '../../__generated__/globalTypes';
import {
  type Job,
  ACTIVE_POLL_INTERVAL,
  BACKGROUND_POLL_INTERVAL,
  calculateETA,
  getJobIcon,
  getJobLabel,
  getStatusColor,
} from './jobUtils';

interface JobsDropdownProps {
  className?: string;
}

export function JobsDropdown({ className }: JobsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data, error, startPolling, stopPolling } = useQuery<
    GetCompanyJobs,
    GetCompanyJobsVariables
  >(GET_COMPANY_JOBS, {
    variables: {
      statuses: [JobStatusEnum.PENDING, JobStatusEnum.IN_PROGRESS],
    },
    pollInterval: BACKGROUND_POLL_INTERVAL, // Always poll to detect new jobs
  });

  const jobs = data?.companyJobs?.jobs ?? [];
  const hasActiveJobs = jobs.length > 0;

  // Log errors for debugging (silent failure is acceptable for non-critical UI)
  useEffect(() => {
    if (error) {
      console.error('[JobsDropdown] Failed to fetch jobs:', error.message);
    }
  }, [error]);

  // Adjust polling speed based on active jobs
  useEffect(() => {
    if (hasActiveJobs) {
      // Poll faster when jobs are in progress
      startPolling(ACTIVE_POLL_INTERVAL);
    } else {
      // Slower background poll to detect new jobs
      startPolling(BACKGROUND_POLL_INTERVAL);
    }

    return () => stopPolling();
  }, [hasActiveJobs, startPolling, stopPolling]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown when all jobs complete to prevent auto-open on new jobs
  useEffect(() => {
    if (!hasActiveJobs) {
      setIsOpen(false);
    }
  }, [hasActiveJobs]);

  if (!hasActiveJobs) {
    return null;
  }

  return (
    <div ref={dropdownRef} className={clsx('relative', className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="focus:ring-primary-500 relative rounded-full p-2 text-gray-300 hover:bg-slate-800 focus:outline-none focus:ring-2"
        aria-label="View active jobs"
      >
        <Spinner size={20} color="#A78BFA" />
        <span className="absolute -right-1 -top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-pink-500 text-xs font-bold text-white shadow-lg">
          {jobs.length}
        </span>
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-96 overflow-hidden rounded-lg border border-gray-700 bg-[#171717] shadow-xl">
          <div className="border-b border-gray-700 bg-[#1f1f1f] px-4 py-3">
            <h3 className="text-sm font-semibold text-white">Active Jobs</h3>
            <p className="mt-1 text-xs text-gray-400">
              {jobs.length} active job{jobs.length !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {jobs.map((job) => {
              const eta = job.status === JobStatusEnum.IN_PROGRESS ? calculateETA(job) : null;

              return (
                <div
                  key={job.job_id}
                  className="border-b border-gray-800 px-4 py-3 last:border-b-0 hover:bg-[#1f1f1f]"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex flex-1 items-start space-x-3">
                      <span className="mt-0.5 text-2xl">{getJobIcon(job.job_type)}</span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-white">
                          {getJobLabel(job)}
                        </p>
                        <p className={clsx('mt-0.5 text-xs', getStatusColor(job.status))}>
                          {job.status === JobStatusEnum.IN_PROGRESS &&
                            `${job.progress_percentage}% complete`}
                          {job.status === JobStatusEnum.PENDING && 'Waiting to start...'}
                        </p>
                        <p className="mt-1 text-xs text-gray-400">
                          {job.processed_count.toLocaleString()} of{' '}
                          {job.estimated_count.toLocaleString()} loans
                        </p>
                        {eta && <p className="mt-1 text-xs italic text-gray-500">{eta}</p>}
                        {job.error_message && (
                          <p className="mt-1 text-xs text-red-400">{job.error_message}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {job.status === JobStatusEnum.IN_PROGRESS && (
                    <div className="mt-2">
                      <div className="h-2 w-full rounded-full bg-gray-700">
                        <div
                          className="h-2 rounded-full bg-gradient-to-r from-violet-500 to-pink-500 transition-all duration-300"
                          style={{ width: `${job.progress_percentage}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
